import { Component, HostListener, Inject, OnInit, PLATFORM_ID, ViewChild, ElementRef, TemplateRef, signal, computed, ChangeDetectorRef, NgZone, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { UserRequestsService } from '../services/user-requests.service';
import { UserRequest } from '../../../models/user-request.model';
import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { UserSelectionModalComponent } from '../user-selection-modal/user-selection-modal.component';
import { UserAvatarComponent } from "../../../shared/user-avatar/user-avatar.component";
import { environment } from '../../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { MessagesService } from '../services/messages.service';
import { SocketService } from '../../../core/services/socket.service';
import { AuthService } from '../../../core/services/auth.service';
import { UserProfile } from '../../../models/user-profile.model';
import { TimeAgoPipe } from "../../../core/pipes/timeAgo.pipe";
import { LinkifyPipe } from "../../../core/pipes/linkify.pipe";
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { EditMessageDialogComponent } from '../edit-message-dialog.component';
import { ChatSkeletonComponent } from "../chat-skeleton.component";
import { MediaSkeletonComponent } from "../media-skeleton.component";
import { UserRequestsModalComponent } from '../user-requests-modal/user-requests-modal.component';
import { UserRequestsPreviewComponent } from "../user-requests-preview/user-requests-preview.component";
import { Conversation, ImageMessage, Message } from '../chat.model';
import { EmojiCategory } from '../services/emoji.constants';
import { APP_ICONS } from '../icons';
import { ToastService } from '../../../core/services/toast.service';
import { ConversationListSkeletonComponent } from '../conversation-list-skeleton.component';
import { ApplicationRef } from '@angular/core';
import { AuthDialogService } from '../../../core/services/auth.dialog.service';
@Component({
  selector: 'app-messages',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgIcon,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatBottomSheetModule,
    MatListModule,
    MatProgressBarModule,
    MatDialogModule,
    MatTooltipModule,
    UserAvatarComponent,
    TimeAgoPipe,
    LinkifyPipe,
    ChatSkeletonComponent,
    MediaSkeletonComponent,
    UserRequestsPreviewComponent,
    ConversationListSkeletonComponent
  ], 
  providers: [provideIcons(APP_ICONS)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './messages.component.html',
  styleUrl: './messages.component.scss'
})
export class MessagesComponent implements OnInit, OnDestroy {
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('fileUploadTemplate') fileUploadTemplate!: TemplateRef<any>;
  @ViewChild(CdkVirtualScrollViewport) virtualScrollViewport!: CdkVirtualScrollViewport;
  private destroy$ = new Subject<void>();
  isMobileView = signal(false);
  showConversationList = signal(true);
  activeConversation = signal<string | null>(null);
  newMessage = signal('');
  searchQuery = signal('');
  chatSearchQuery = signal('');
  collapsedSidebar = signal(false);
  isLoading = signal(false);
  isBrowser = signal(false);
  isTyping = signal(false);
  showChatSearch = signal(false);
  isUploading = signal(false);
  replyToMessage = signal<Message | null>(null);
  selectedFiles = signal<File[]>([]);
  uploadProgress = signal<{ [key: string]: number }>({});
  searchResults = signal<Message[]>([]);
  currentSearchIndex = signal(0);
  typingTimeout: any;
  connectionStatus = signal<'online' | 'offline' | 'connecting'>('online');
  selectedMessage = signal<Message | null>(null);
  profileApiUrl = environment.profileApiUrl;
  conversations = signal<Conversation[]>([]);
  private messageInputSubject = new Subject<string>();
  private scrollToBottomSubject = new Subject<void>();
  private shouldAutoScroll = true;
  private currentPage = 1;
  private hasMoreMessages = true;
  private messagesPerPage = 50;
  loggedUser: UserProfile | null = null;
  currentUserId: String | null = null;
  messages = signal<Message[]>([]);
  private audioElements = new Map<string, HTMLAudioElement>();
  activeFilter = signal<'all' | 'unread' | 'active'>('all');
  private isNearBottom = true;
  emojiCategories = EmojiCategory;
  filteredUserRequests = signal<UserRequest[]>([]);
  userRequests = signal<UserRequest[]>([]);
  conversationsLoading = signal(false);
  filteredConversations = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const filter = this.activeFilter();
    const convs = this.conversations() ?? [];

    let filtered = convs.filter(conv =>
      (conv?.firstName?.toLowerCase() ?? '').includes(query) ||
      (conv?.lastName?.toLowerCase() ?? '').includes(query) ||
      (conv?.company?.toLowerCase() ?? '').includes(query) ||
      (conv?.lastMessage?.toLowerCase() ?? '').includes(query)
    );

    switch (filter) {
      case 'unread':
        filtered = filtered.filter(conv => conv.unreadCount > 0);
        break;
      case 'active':
        filtered = filtered.filter(conv => conv.isOnline);
        break;
      case 'all':
      default:
        break;
    }

    return filtered.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      if (a.unreadCount > b.unreadCount) return -1;
      if (a.unreadCount < b.unreadCount) return 1;
      return new Date(b.time).getTime() - new Date(a.time).getTime();
    });
  });

  activeConversationData = computed(() => {
    const activeId = this.activeConversation();
    return this.conversations().find(conv => conv._id === activeId);
  });
  isViewportReady = false;
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private bottomSheet: MatBottomSheet,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private dialog: MatDialog,
    private router: Router,
    private messagesService: MessagesService,
    private activatedRoute: ActivatedRoute,
    private socketService: SocketService,
    private authService: AuthService,
    private userRequestsService: UserRequestsService,
    private toastService: ToastService,
    public authDialog: AuthDialogService,
    // private appRef: ApplicationRef

  ) {
    this.isBrowser.set(isPlatformBrowser(this.platformId));
    if (this.isBrowser()) {
      this.checkViewport();
      this.isViewportReady = true;
    }
    this.ngZone.onStable.subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  private handleViewRequestsParam() {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(queryParams => {
        const viewRequests = queryParams['viewRequests'];

        if (viewRequests) {
          let filterStatus = 'pending';
          let type = 'incoming';

          if (viewRequests === 'all') {
            filterStatus = 'all';
          } else if (viewRequests === 'pending') {
            filterStatus = 'pending';
          } else if (viewRequests === 'accepted') {
            filterStatus = 'accepted';
            type = 'outgoing';
          } else if (viewRequests === 'rejected') {
            filterStatus = 'rejected';
            type = 'outgoing';
          }


          this.userRequestsService.getUserRequests(1, 50, filterStatus, type).subscribe({
            next: (requests) => {
              let filtered = requests.filter(el => el.status == 'pending');
              this.filteredUserRequests.set(filtered);
              this.userRequests.set(requests);
              setTimeout(() => {
                if (viewRequests === 'true' || viewRequests === 'all') {
                  this.openUserRequestsModalWithFilter('all');
                } else if (viewRequests === 'pending') {
                  this.openUserRequestsModalWithFilter('pending');
                } else if (viewRequests === 'accepted') {
                  this.openUserRequestsModalWithFilter('accepted');
                } else if (viewRequests === 'rejected') {
                  this.openUserRequestsModalWithFilter('rejected');
                }

                this.router.navigate([], {
                  queryParams: { viewRequests: null },
                  queryParamsHandling: 'merge',
                  replaceUrl: true
                });
              }, 300);
            },
            error: (error) => {
              console.error('Failed to load user requests:', error);
            }
          });

        }
      });
  }

  ngOnInit() {
    if (this.isBrowser()) {
      // this.checkViewport();
      this.conversationsLoading.set(true);

      this.initializeSocket();
      this.handleViewRequestsParam();
      this.messageInputSubject
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.handleTypingIndicator();
        });

      this.scrollToBottomSubject
        .pipe(
          debounceTime(100),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.performScrollToBottom();
        });
    }

  }
  private checkViewport(): void {
    if (!this.isBrowser()) return;

    this.isMobileView.set(window.innerWidth < 768);
    const activeId = this.activeConversation();

    if (!this.isMobileView()) {
      this.showConversationList.set(true);
    } else {
      if (!activeId) {
        this.showConversationList.set(true);
      } else {
        this.showConversationList.set(false);
      }
    }
  }

  getUnderlineFilterClasses(filterType: 'all' | 'unread' | 'active'): string {
    const isActive = this.activeFilter() === filterType;
    const baseClasses = 'tw-flex tw-items-center tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-transition-all tw-duration-200 tw-border-b-2 tw-mr-6';

    if (isActive) {
      return `${baseClasses} tw-border-blue-500 tw-text-blue-600 dark:tw-text-blue-400`;
    }

    return `${baseClasses} tw-border-transparent tw-text-gray-600 dark:tw-text-gray-400 hover:tw-text-gray-900 dark:hover:tw-text-white hover:tw-border-gray-300 dark:hover:tw-border-gray-600`;
  }

  private initializeSocket() {
    this.authService.user$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id)
      )
      .subscribe(user => {
        this.currentUserId = user?.id || null
        if (!user) {
          return;
        }
        this.loggedUser = (user as UserProfile);
        this.loadUserRequests();

        this.socketService.conversationCreated$
          .pipe(takeUntil(this.destroy$))
          .subscribe(result => {
            if (result) {
              const { conversation, isNew } = result;
              const userName = `${conversation.firstName}${conversation.lastName}`
                .toLowerCase()
                .replace(/\s+/g, '');
              this.router.navigate(['/messages', userName]);
            }
          })

        this.socketService.conversationError$
          .pipe(takeUntil(this.destroy$))
          .subscribe(error => {
            if (error) {
              this.toastService.error(error.error || 'Failed to create conversation');
            }
          });

        this.socketService.conversations$
          .pipe(takeUntil(this.destroy$))
          .subscribe(conversations => {
            if (conversations) {
              this.conversations.set(conversations);
              this.conversationsLoading.set(false);
              this.isLoading.set(false);
              this.handleRouteAfterConversationsLoad();
            }
          });

        this.socketService.messageHistory$
          .pipe(takeUntil(this.destroy$))
          .subscribe(data => {
            if (data) {
              this.handleMessageHistory(data);
            }
          });

        this.socketService.messageSent$
          .pipe(takeUntil(this.destroy$))
          .subscribe(data => {
            if (data) {
              const newMsg: Message = {
                _id: data.message.messageId || data.message._id || new Date().toISOString(),
                sender: data.message.senderName || data.message.sender || 'Unknown User',
                senderAvatar: data.message.senderAvatar || 'Avatar0',
                content: data.message.content,
                reactions: data.message.reactions || [],
                createdAt: data.message.timestamp || data.message.createdAt || new Date().toISOString(),
                updatedAt: data.message.updatedAt || new Date().toISOString(),
                isEdited: data.message.isEdited || false,
                editedAt: data.message.editedAt,
                isDeleted: data.message.isDeleted || false,
                deletedAt: data.message.deletedAt,
                isRead: data.message.isRead || false,
                type: data.message.messageType || data.message.type || 'text',
                status: data.message.status || 'delivered',
                userId: data.message.senderId || data.message.userId,
                replyToId: data.message.replyToId,
                replyToContent: data.message.replyToContent,
                replyToSender: data.message.replyToSender,
                fileId: data.message.fileId,
                fileName: data.message.fileName,
                fileSize: data.message.fileSize,
                contentType: data.message.contentType,
                hasFile: data.message.hasFile,
                fileInfo: data.message.fileInfo,
              };
              this.messages.update(prev =>
                prev.map(msg =>
                  msg._id === data.tempId
                    ? { ...newMsg, status: 'sent' }
                    : msg
                )
              );
            }
          });

        this.socketService.messages$
          .pipe(takeUntil(this.destroy$))
          .subscribe(message => {
            if (message) {
              this.handleIncomingMessage(message);
            }
          });

        this.socketService.messageStatus$
          .pipe(takeUntil(this.destroy$))
          .subscribe(statusUpdate => {
            if (statusUpdate) {
              this.updateLocalMessageStatus(statusUpdate);
            }
          });

        this.socketService.typingStatus$
          .pipe(takeUntil(this.destroy$))
          .subscribe(typingData => {
            if (typingData) {
              this.updateTypingStatus(typingData);
            }
          });

        this.socketService.conversationUpdated$
          .pipe(takeUntil(this.destroy$))
          .subscribe(update => {
            if (update) {
              this.updateSingleConversation(update);
            }
          });

        this.socketService.userOnlineStatus$
          .pipe(takeUntil(this.destroy$))
          .subscribe(status => {
            if (status) {
              this.updateUserOnlineStatus(status.userId, status.isOnline, status);
            }
          });

        this.socketService.messageEdited$
          .pipe(takeUntil(this.destroy$))
          .subscribe(editedMessage => {
            if (editedMessage) {
              this.handleMessageEdited(editedMessage);
            }
          });

        this.socketService.editMessageSuccess$
          .pipe(takeUntil(this.destroy$))
          .subscribe(data => {
            if (data) {
              this.handleMessageEdited(data);
              this.toastService.success('Message edited successfully');
            }
          });

        this.socketService.editMessageError$
          .pipe(takeUntil(this.destroy$))
          .subscribe(error => {
            if (error) {
              this.toastService.error(error.error || 'Failed to edit message');
            }
          });
        this.socketService.messageDeleted$
          .pipe(takeUntil(this.destroy$))
          .subscribe(deletedMessage => {
            if (deletedMessage) {
              this.handleMessageDeleted(deletedMessage);
            }
          });

        this.socketService.deleteMessageSuccess$
          .pipe(takeUntil(this.destroy$))
          .subscribe(data => {
            if (data) {
              this.handleMessageDeleted(data);
              this.toastService.success('Message deleted successfully');
            }
          });

        this.socketService.deleteMessageError$
          .pipe(takeUntil(this.destroy$))
          .subscribe(error => {
            if (error) {
              this.toastService.error(error.error || 'Failed to delete message');
            }
          });

        this.socketService.fileUploadSuccess$
          .pipe(takeUntil(this.destroy$))
          .subscribe(response => {
            if (response) {
              this.handleFileUploadSuccess(response);
            }
          });

        this.socketService.fileUploadError$
          .pipe(takeUntil(this.destroy$))
          .subscribe(error => {
            if (error) {
              this.toastService.error(error.error || 'File upload failed');
            }
          });

        this.socketService.fileData$
          .pipe(takeUntil(this.destroy$))
          .subscribe(fileData => {
            if (fileData) {
              this.handleFileData(fileData);
            }
          });

        this.socketService.fileError$
          .pipe(takeUntil(this.destroy$))
          .subscribe(error => {
            if (error) {
              this.toastService.error(error.error || 'Failed to retrieve file');
            }
          });
      });

  }

  private loadUserRequestsWithFilter(status: string = 'pending', type: string = 'incoming') {
    this.userRequestsService.getUserRequests(1, 50, status, type).subscribe({
      next: (requests) => {
        let filtered = requests.filter(el => el.status == 'pending');
        this.filteredUserRequests.set(filtered);
        this.userRequests.set(requests);
      },
      error: (error) => {
        console.error('Failed to load user requests:', error);
      }
    });
  }

  private loadUserRequests() {
    this.loadUserRequestsWithFilter('pending');
  }

  openUserRequestsModal() {
    const dialogRef = this.dialog.open(UserRequestsModalComponent, {
      width: '800px',
      maxWidth: '98vw',
      maxHeight: '98vh',
      autoFocus: false,
      restoreFocus: false,
      panelClass: ['safety-knights-dialog'],
      hasBackdrop: true,
      data: {
        requests: this.userRequests()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.action === 'accept') {
          this.acceptUserRequest(result.request);
        } else if (result.action === 'reject') {
          this.rejectUserRequest(result.request);
        }
      }
    });
  }

  openUserRequestsModalWithFilter(filter: string = 'all') {
    const dialogRef = this.dialog.open(UserRequestsModalComponent, {
      width: '800px',
      maxWidth: '98vw',
      maxHeight: '98vh',
      autoFocus: false,
      restoreFocus: false,
      panelClass: ['safety-knights-dialog'],
      hasBackdrop: true,
      data: {
        requests: this.userRequests(),
        initialFilter: filter
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.action === 'accept') {
          this.acceptUserRequest(result.request);
        } else if (result.action === 'reject') {
          this.rejectUserRequest(result.request);
        }
      }
    });
  }

  acceptUserRequest(request: UserRequest) {
    this.conversationsLoading.set(true);
    this.userRequestsService.acceptRequest(request._id).subscribe({
      next: (response) => {
        if (response.success && this.loggedUser?.id) {
          this.userRequestsService.updateRequestStatus(request._id, 'accepted');
          this.socketService.sendConnectionAccepted({
            fromUserId: request.fromUser._id,
            toUserId: this.loggedUser?.id,
            acceptedByUserId: this.loggedUser?.id
          });
          this.toastService.success(`Accepted connection request from ${request.fromUser.firstName}`);
          this.handleAcceptedConnection(response.request?.fromUser);
        }
      },
      error: (error) => {
        console.error('Failed to accept request:', error);
        this.toastService.error(`Failed to accept connection request`);
      }
    });
  }

  rejectUserRequest(request: UserRequest) {
    this.conversationsLoading.set(true);
    this.userRequestsService.rejectRequest(request._id).subscribe({
      next: (response) => {
        if (response.success && this.loggedUser?.id) {
          this.userRequestsService.updateRequestStatus(request._id, 'rejected');
          this.loadUserRequests();
          this.socketService.sendConnectionRejected({
            fromUserId: request.fromUser._id,
            toUserId: this.loggedUser?.id,
            rejectedByUserId: this.loggedUser?.id
          });
          this.toastService.success(`Declined connection request from ${request.fromUser.firstName}`);
        }
      },
      error: (error) => {
        console.error('Failed to reject request:', error);
        this.toastService.error(`Failed to decline connection request`);
      }
    });
  }

  private handleAcceptedConnection(user: any) {
    this.handleNewConversation(user);
    console.log('Connection accepted, you can now message each other');
  }

  onMessageInput() {
    this.messageInputSubject.next(this.newMessage());
  }

  private scrollToBottom() {
    this.scrollToBottomSubject.next();
  }

  onVirtualScrollChange(index: any) {
    const totalMessages = this.messages().length;
    this.isNearBottom = index >= totalMessages - 5;
    this.shouldAutoScroll = this.isNearBottom;
  }

  private performScrollToBottom() {
    if (!this.isBrowser() || !this.shouldAutoScroll) return;

    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.ngZone.run(() => {
          if (this.virtualScrollViewport) {
            const lastIndex = Math.max(0, this.messages().length - 1);
            this.virtualScrollViewport.scrollToIndex(lastIndex, 'smooth');
          } else {
            const container = document.querySelector('.tw-messages-container');
            if (container) {
              container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth'
              });
            }
          }
        });
      });
    });
  }

  private handleTypingIndicator() {
    const activeId = this.activeConversation();
    if (activeId) {
      this.socketService.startTyping(activeId);
    }

    if (!this.isTyping()) {
      this.isTyping.set(true);
    }

    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    this.typingTimeout = setTimeout(() => {
      this.isTyping.set(false);
      if (activeId) {
        this.socketService.stopTyping(activeId);
      }
    }, 1000);
  }


  private handleFileUploadSuccess(response: any) {
    this.ngZone.run(() => {
      console.log('File uploaded successfully:', response);

      if (response.message) {
        const newMsg: Message = {
          _id: response.message.messageId,
          sender: response.message.sender,
          senderAvatar: response.message.senderAvatar,
          content: response.message.content,
          reactions: response.message.reactions || [],
          createdAt: response.message.createdAt,
          updatedAt: response.message.updatedAt,
          isEdited: false,
          isDeleted: false,
          isRead: response.message.isRead,
          type: response.message.type,
          status: response.message.status,
          userId: response.message.senderId,
          fileId: response.message.fileId,
          fileName: response.message.fileName,
          fileSize: response.message.fileSize,
          contentType: response.message.contentType,
          hasFile: response.message.hasFile,
          fileInfo: response.message.fileInfo,
        };

        this.messages.update(prev => [...prev, newMsg]);

        if (newMsg.fileId && newMsg.type !== 'text' && !newMsg.isDeleted) {
          this.socketService.getFile(newMsg.fileId).then(fileData => {
            this.handleFileData(fileData);
          }).catch(error => {
            console.error('Failed to load file after upload:', error);
          });
        }

        this.updateConversationLastMessage(newMsg);

        this.shouldAutoScroll = true;
        setTimeout(() => this.scrollToBottom(), 200);

        this.cdr.markForCheck();
      }
    });
  }

  private async handleFileData(fileData: any) {
    this.ngZone.run(() => {
      const blob = this.base64ToBlob(fileData.data, fileData.contentType || 'application/octet-stream');
      const blobUrl = URL.createObjectURL(blob);

      this.messages.update(prev =>
        prev.map(msg => {
          if (msg.fileId === fileData.fileId) {
            if (msg.fileUrl) {
              URL.revokeObjectURL(msg.fileUrl);
            }

            return {
              ...msg,
              fileUrl: blobUrl,
              thumbnailUrl: fileData.contentType?.startsWith('image/') ? blobUrl : undefined,
              status: 'delivered'
            };
          }
          return msg;
        })
      );

      this.cdr.markForCheck();
    });
  }

  private base64ToBlob(base64: string, contentType: string): Blob {
    const base64Data = base64.split(',')[1];
    const sliceSize = 1024;
    const byteChars = atob(base64Data);
    const byteArrays: Uint8Array[] = [];

    for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
      const slice = byteChars.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays as BlobPart[], { type: contentType });
  }



  private handleMessageEdited(editedMessageData: any) {
    this.ngZone.run(() => {
      this.messages.update(prev =>
        prev.map(msg => {
          if (msg._id === editedMessageData.messageId) {
            return {
              ...msg,
              content: editedMessageData.content,
              isEdited: true,
              editedAt: editedMessageData.editedAt,
              updatedAt: editedMessageData.updatedAt
            };
          }
          return msg;
        })
      );

      this.updateConversationOnEdit(editedMessageData);

      this.cdr.markForCheck();
    });
  }

  private updateConversationOnEdit(editedMessageData: any) {
    const activeId = this.activeConversation();
    if (!activeId) return;

    const conversation = this.conversations().find(conv => conv._id === activeId);
    if (conversation && conversation.lastMessage) {
      const messages = this.messages();
      const lastMessage = messages[messages.length - 1];

      if (lastMessage && lastMessage._id === editedMessageData.messageId) {
        this.conversations.update(prev =>
          prev.map(conv => {
            if (conv._id === activeId) {
              return {
                ...conv,
                lastMessage: editedMessageData.content,
                time: editedMessageData.updatedAt
              };
            }
            return conv;
          })
        );
      }
    }
  }

  private handleMessageDeleted(deletedMessageData: any) {
    this.ngZone.run(() => {
      this.messages.update(prev =>
        prev.map(msg => {
          if (msg._id === deletedMessageData.messageId) {
            return {
              ...msg,
              isDeleted: true,
              deletedAt: deletedMessageData.deletedAt,
              updatedAt: deletedMessageData.updatedAt
            };
          }
          return msg;
        })
      );

      if (this.replyToMessage()?._id === deletedMessageData.messageId) {
        this.replyToMessage.set(null);
      }

      this.cdr.markForCheck();
    });
  }

  deleteMessage(message: Message): void {
    if (confirm('Delete this message?')) {
      const activeId = this.activeConversation();
      if (activeId && message._id) {
        this.socketService.deleteMessage(message._id, activeId);
      }
    }
  }


  private updateSingleConversation(update: any) {
    const conversations = this.conversations();
    const updatedConversations = conversations.map(conv => {
      if (conv._id === update.chatId) {
        return {
          ...conv,
          lastMessage: update.lastMessage,
          lastMessageSender: update.senderId === this.loggedUser?.id ? 'You' : update.lastMessageSender,
          time: update.time,
          unreadCount: update.senderId === this.loggedUser?.id ? conv.unreadCount : conv.unreadCount + 1
        };
      }
      return conv;
    });

    updatedConversations.sort((a, b) =>
      new Date(b.time).getTime() - new Date(a.time).getTime()
    );

    this.conversations.set(updatedConversations);
    this.cdr.markForCheck();
  }

  private handleRouteAfterConversationsLoad() {
    this.activatedRoute.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const userName = params['userName'];
        const messageId = params['messageId'];

        if (userName) {
          setTimeout(() => {
            this.handleRouteParameters(userName, messageId);
          }, 100);
        }
      });
  }

  private handleIncomingMessage(socketMessage: any) {
    const existingMessage = this.messages().find(msg =>
      msg._id === socketMessage.messageId ||
      msg._id === socketMessage._id
    );

    if (existingMessage) {
      console.log('Duplicate message ignored');
      return;
    }

    const newMsg: Message = {
      _id: socketMessage.messageId || socketMessage._id || new Date().toISOString(),
      sender: socketMessage.senderName || socketMessage.sender || 'Unknown User',
      senderAvatar: socketMessage.senderAvatar || 'Avatar0',
      content: socketMessage.content,
      reactions: socketMessage.reactions || [],
      createdAt: socketMessage.timestamp || socketMessage.createdAt || new Date().toISOString(),
      updatedAt: socketMessage.updatedAt || new Date().toISOString(),
      isEdited: socketMessage.isEdited || false,
      editedAt: socketMessage.editedAt,
      isDeleted: socketMessage.isDeleted || false,
      deletedAt: socketMessage.deletedAt,
      isRead: socketMessage.isRead || false,
      type: socketMessage.messageType || socketMessage.type || 'text',
      status: socketMessage.status || 'delivered',
      userId: socketMessage.senderId || socketMessage.userId,
      replyToId: socketMessage.replyToId,
      replyToContent: socketMessage.replyToContent,
      replyToSender: socketMessage.replyToSender,
      fileId: socketMessage.fileId,
      fileName: socketMessage.fileName,
      fileSize: socketMessage.fileSize,
      contentType: socketMessage.contentType,
      hasFile: socketMessage.hasFile,
      fileInfo: socketMessage.fileInfo,
    };

    this.ngZone.run(() => {
      this.messages.update(prev => [...prev, newMsg]);

      // if (newMsg.fileId && newMsg.type !== 'text' && !newMsg.isDeleted) {
      //   this.socketService.getFile(newMsg.fileId).then(fileData => {
      //     this.handleFileData(fileData);
      //   }).catch(error => {
      //     console.error('Failed to load file:', error);
      //   });
      // }

      if (newMsg?.userId != this.loggedUser?.id) {
        this.updateConversationLastMessage(newMsg);
      }

      if (this.isNearBottom || newMsg?.userId == this.loggedUser?.id) {
        this.shouldAutoScroll = true;
        setTimeout(() => this.scrollToBottom(), 200);
      }

      this.cdr.markForCheck();
    });
  }


  private updateConversationSidebar(update: any) {
    const conversations = this.conversations();
    const updatedConversations = conversations.map(conv => {
      if (conv._id === update.chatId) {
        return {
          ...conv,
          lastMessage: update.lastMessage,
          lastMessageSender: update.lastMessageSender,
          time: update.time,
          unreadCount: conv.unreadCount + (update.isFromCurrentUser ? 0 : 1)
        };
      }
      return conv;
    });

    this.conversations.set(updatedConversations);
    this.cdr.markForCheck();
  }

  private async handleMessageHistory(data: any) {
    const { messages, hasMore, page } = data;

    const formattedMessages = messages.map((msg: any) => ({
      _id: msg._id,
      conversationId: msg.conversationId,
      sender: `${msg.senderId.firstName} ${msg.senderId.lastName}`,
      senderAvatar: msg.senderId.profilePictureKey || '',
      senderOnline: msg.senderId.isOnline,
      senderLastSeen: msg.senderId.lastSeen,
      content: msg.content,
      reactions: msg.reactions || [],
      createdAt: msg.createdAt,
      updatedAt: msg.updatedAt,
      isEdited: msg.isEdited ?? false,
      editedAt: msg.editedAt,
      isDeleted: msg.isDeleted || false,
      deletedAt: msg.deletedAt,
      isRead: msg.isRead,
      type: msg.type,
      status: msg.status || 'delivered',
      userId: msg.senderId._id,
      replyToId: msg.replyToId || undefined,
      replyToContent: msg.replyToContent,
      replyToSender: msg.replyToSender,
      fileId: msg.fileId,
      fileName: msg.fileName,
      fileSize: msg.fileSize,
      contentType: msg.contentType,
      hasFile: msg.hasFile,
      fileInfo: msg.fileInfo,
    }));

    if (page === 1) {
      this.messages.set(formattedMessages);
      setTimeout(() => this.scrollToBottom(), 200);
    } else {
      this.messages.update(prev => [...formattedMessages, ...prev]);
    }
    // setTimeout(async () => {
    //   const messagesWithFiles = formattedMessages.filter((msg: Message) => msg.fileId);
    //   for (const msg of messagesWithFiles) {
    //     if (!msg.isDeleted) {
    //       try {
    //         const fileData = await this.socketService.getFile(msg.fileId);
    //         await this.handleFileData({ ...fileData, messageId: msg._id });
    //       } catch (error) {
    //         console.error('Failed to load file for message:', msg._id, error);
    //       }
    //     }
    //   }
    // }, 3000);

    this.hasMoreMessages = hasMore;
    this.currentPage = page + 1;
    this.isLoading.set(false);
  }


  private updateLocalMessageStatus(statusUpdate: any) {
    this.messages.update(prev =>
      prev.map(msg =>
        msg._id === statusUpdate.messageId ? { ...msg, status: statusUpdate.status } : msg
      )
    );
  }

  async sendMessage(event?: Event) {
    if (event) event.preventDefault();

    const message = this.newMessage().trim();
    if (!message) return;

    const replyToMsg = this.replyToMessage();
    const activeId = this.activeConversation();
    if (!activeId) return;

    try {
      const now = new Date();

      const optimisticMessage: Message = {
        _id: now.toISOString(),
        sender: 'You',
        senderAvatar: this.loggedUser?.profilePictureKey || 'Avatar0',
        content: message,
        reactions: [],
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        isRead: true,
        isEdited: false,
        isDeleted: false,
        type: 'text',
        status: 'sending',
        replyToId: replyToMsg?._id || undefined,
        replyToContent: replyToMsg?.content,
        replyToSender: replyToMsg?.sender,
        userId: this.loggedUser?.id || '',
      };

      this.socketService.sendMessage(activeId, message, 'text', replyToMsg?._id, now.toISOString());

      this.messages.update(prev => [...prev, optimisticMessage]);

      this.shouldAutoScroll = true;
      this.isNearBottom = true;

      this.ngZone.run(() => setTimeout(() => this.scrollToBottom(), 200));

      this.updateConversationLastMessage(optimisticMessage);

      this.newMessage.set('');
      this.replyToMessage.set(null);
      this.adjustTextareaHeight(true);
    } catch (error) {
      console.error('Error sending message:', error);
      this.toastService.error('Error sending message');
    }
  }

  trackMessage(index: number, message: Message): string {
    return message._id + message.updatedAt + index;
  }

  private updateConversationLastMessage(message: Message) {
    const activeId = this.activeConversation();
    if (activeId) {
      const updatedConversations = this.conversations().map(conv => {
        if (conv._id === activeId) {
          return {
            ...conv,
            lastMessage: message.content,
            lastMessageSender: message.sender,
            time: new Date().toISOString(),
            unreadCount: (this.loggedUser && message.userId == this.loggedUser?.id) ? 0 : conv.unreadCount + 1
          };
        }
        return conv;
      });
      this.conversations.set(updatedConversations);
    }
  }

  searchInChat() {
    const query = this.chatSearchQuery().trim();
    if (!query) {
      this.searchResults.set([]);
      this.showChatSearch.set(false);
      return;
    }

    this.showChatSearch.set(true);
    const results = this.messages().filter(message =>
      message.content.toLowerCase().includes(query.toLowerCase())
    );
    this.searchResults.set(results);
    this.currentSearchIndex.set(0);

    if (results.length > 0) {
      this.scrollToSearchResult(0);
    }
  }

  navigateSearchResults(direction: 'next' | 'prev') {
    const results = this.searchResults();
    if (results.length === 0) return;

    let newIndex: number;
    if (direction === 'next') {
      newIndex = (this.currentSearchIndex() + 1) % results.length;
    } else {
      newIndex = this.currentSearchIndex() === 0
        ? results.length - 1
        : this.currentSearchIndex() - 1;
    }

    this.currentSearchIndex.set(newIndex);
    this.scrollToSearchResult(newIndex);
  }

  scrollToSearchResult(index: number) {
    const results = this.searchResults();
    const messageId = results[index]._id;

    if (this.isBrowser()) {
      const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        messageElement.classList.add('tw-bg-yellow-200/50', 'tw-transition-colors', 'tw-duration-500');
        setTimeout(() => {
          messageElement.classList.remove('tw-bg-yellow-200/50');
        }, 2000);
      }
    }
  }

  clearChatSearch() {
    this.chatSearchQuery.set('');
    this.searchResults.set([]);
    this.showChatSearch.set(false);
    this.currentSearchIndex.set(0);
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.selectedFiles.set(Array.from(input.files));
      this.openFileUploadSheet();
    }
  }

  openFileUploadSheet() {
    const bottomSheetRef = this.bottomSheet.open(FileUploaderComponent, {
      data: {
        files: this.selectedFiles(),
        config: {
          maxFiles: 5,
          maxFileSize: 30 * 1024 * 1024,
          allowedTypes: ['image/*', 'application/pdf'],
          multiple: true
        }
      }
    });

    bottomSheetRef.afterDismissed().subscribe((result) => {
      if (result && result.files) {
        this.uploadFiles(result.files);
      }
      this.selectedFiles.set([]);
    });
  }

  async uploadFiles(files: File[]) {
    this.isUploading.set(true);
    const activeId = this.activeConversation();

    if (!activeId) {
      this.isUploading.set(false);
      return;
    }

    const uploadPromises = files.map(file => this.uploadSingleFile(file, activeId));

    try {
      await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Some files failed to upload:', error);
    } finally {
      this.isUploading.set(false);
    }
  }

  private async uploadSingleFile(file: File, chatId: string): Promise<void> {
    const fileId = `file_${Date.now()}_${Math.random()}`;
    let progressInterval: NodeJS.Timeout | null = null;

    return new Promise(async (resolve, reject) => {
      try {
        const progress = { ...this.uploadProgress() };
        progress[fileId] = 0;
        this.uploadProgress.set(progress);

        progressInterval = setInterval(() => {
          const currentProgress = this.uploadProgress()[fileId] || 0;
          if (currentProgress < 90) {
            const updatedProgress = { ...this.uploadProgress() };
            updatedProgress[fileId] = Math.min(90, currentProgress + Math.random() * 15);
            this.uploadProgress.set(updatedProgress);
          }
        }, 300);

        try {
          await this.socketService.uploadFile(file, chatId);
          if (progressInterval) {
            clearInterval(progressInterval);
            progressInterval = null;
          }

          const finalProgress = { ...this.uploadProgress() };
          finalProgress[fileId] = 100;
          this.uploadProgress.set(finalProgress);

          setTimeout(() => {
            const newProgress = { ...this.uploadProgress() };
            delete newProgress[fileId];
            this.uploadProgress.set(newProgress);
          }, 1000);

          resolve();

        } catch (uploadError) {
          console.error(`Upload failed for file: ${file.name}`, uploadError);
          throw uploadError;
        }

      } catch (error) {
        console.error(`Error in uploadSingleFile for ${file.name}:`, error);

        if (progressInterval) {
          clearInterval(progressInterval);
        }
        const newProgress = { ...this.uploadProgress() };
        delete newProgress[fileId];
        this.uploadProgress.set(newProgress);
        this.toastService.error(`Failed to upload ${file.name}: ${error}`);
        reject(error);
      }
    });
  }


  onImageLoadError(message: Message) {
    this.messages.update(prev =>
      prev.map(msg =>
        msg._id === message._id
          ? { ...msg, imageLoadError: true }
          : msg
      )
    );
  }

  addEmoji(emoji: string) {
    this.newMessage.set(this.newMessage() + emoji);
    if (this.messageInput?.nativeElement) {
      this.messageInput.nativeElement.focus();
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(message: Message): string {
    if (!message.fileName) return 'heroDocument';

    const fileName = message.fileName.toLowerCase();
    if (message.type === 'image') return 'heroPhoto';
    if (fileName.includes('.pdf')) return 'heroDocumentText';
    if (fileName.includes('.zip') || fileName.includes('.rar')) return 'heroArchiveBox';
    if (fileName.includes('.doc') || fileName.includes('.docx')) return 'heroDocument';
    if (fileName.includes('.xls') || fileName.includes('.xlsx')) return 'heroTableCells';
    return 'heroDocument';
  }

  downloadFile(message: Message) {
    if (message.fileId && message.fileName && this.isBrowser()) {
      this.socketService.getFile(message.fileId).then(fileData => {
        const blob = this.base64ToBlob(fileData.data, fileData.contentType);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = message.fileName!;
        link.click();
        URL.revokeObjectURL(url);
      }).catch(error => {
        console.error('Failed to download file:', error);
        this.toastService.error('Failed to download file');
      });
    }
  }

  setReplyMessage(message: Message) {
    this.replyToMessage.set(message);
    if (this.messageInput?.nativeElement) {
      this.messageInput.nativeElement.focus();
    }
  }

  cancelReply() {
    this.replyToMessage.set(null);
  }

  @HostListener('window:resize')
  onResize() {
    this.checkViewport();
  }




  selectConversation(conversationId: string) {
    this.activeConversation.set(conversationId);

    const conversation = this.conversations().find(conv => conv._id === conversationId);
    if (conversation) {
      const userName = `${conversation.firstName}${conversation.lastName}`.toLowerCase().replace(/\s+/g, '');
      this.router.navigate(['/messages', userName], { replaceUrl: true });
    }

    if (this.isMobileView()) {
      this.showConversationList.set(false);
    }

    this.socketService.joinChat(conversationId);

    this.socketService.markAllMessagesAsRead(conversationId);

    this.loadMessagesForConversation(conversationId);
  }


  private updateTypingStatus(data: any) {
    const updatedConversations = this.conversations().map(conv => {
      if (conv._id === data.chatId) {
        return { ...conv, isTyping: data.isTyping };
      }
      return conv;
    });
    this.conversations.set(updatedConversations);
  }

  private updateUserOnlineStatus(userId: string, isOnline: boolean, status: any) {
    const updatedConversations = this.conversations().map(conv => {

      if (conv._id === userId) {
        return { ...conv, isOnline };
      }
      return conv;
    });
    this.conversations.set(updatedConversations);
  }

  private async loadMessagesForConversation(conversationId: string, loadMore: boolean = false): Promise<void> {
    if (!loadMore) {
      this.currentPage = 1;
      this.hasMoreMessages = true;
      this.messages.set([])
    }

    this.socketService.requestMessages(conversationId, this.currentPage, this.messagesPerPage);

    this.isLoading.set(true);
  }


  private loadConversations(): void {
    this.conversationsLoading.set(true);
    this.isLoading.set(true);
    this.socketService.requestConversations();
  }

  async loadOlderMessages(): Promise<void> {
    if (!this.hasMoreMessages || this.isLoading()) return;

    const activeId = this.activeConversation();
    if (activeId) {
      await this.loadMessagesForConversation(activeId, true);
    }
  }

  backToConversations() {
    this.showConversationList.set(true);
    this.activeConversation.set(null);
    this.router.navigate(['/messages'], { replaceUrl: true });
  }

  private async handleRouteParameters(userName: string, messageId?: string): Promise<void> {
    try {
      let conversation = this.conversations().find(conv =>
        conv.firstName.toLowerCase().replace(/\s+/g, '') === userName.toLowerCase() ||
        conv.lastName.toLowerCase().replace(/\s+/g, '') === userName.toLowerCase() ||
        `${conv.firstName}${conv.lastName}`.toLowerCase().replace(/\s+/g, '') === userName.toLowerCase()
      );

      if (!conversation) {
        try {
          conversation = await this.messagesService.getConversationByUsername(userName).toPromise();

          if (conversation) {
            const currentConversations = this.conversations();
            this.conversations.set([conversation, ...currentConversations]);
          }
        } catch (error) {
          console.error('Conversation not found:', error);
          this.toastService.error(`User ${userName} not found`);
          this.router.navigate(['/messages']);
          return;
        }
      }

      if (conversation) {
        this.activeConversation.set(conversation._id);

        if (this.isMobileView()) {
          this.showConversationList.set(false);
        }

        const updatedConversations = this.conversations().map(conv => {
          if (conv._id === conversation!._id) {
            return { ...conv, unreadCount: 0 };
          }
          return conv;
        });
        this.conversations.set(updatedConversations);
        this.socketService.joinChat(conversation._id);

        this.socketService.markAllMessagesAsRead(conversation._id);
        await this.loadMessagesForConversation(conversation._id);

      }
    } catch (error) {
      console.error('Error handling route parameters:', error);
    }
  }

  adjustTextareaHeight(reset = false) {
    if (!this.messageInput?.nativeElement) return;

    const textarea = this.messageInput.nativeElement;
    if (reset) {
      textarea.style.height = 'auto';
    } else {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px';
    }
  }

  getSendButtonClasses() {
    return this.newMessage().trim()
      ? 'tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 hover:tw-from-blue-600 hover:tw-to-blue-700 tw-text-white tw-shadow-lg hover:tw-shadow-xl hover:tw-scale-105 active:tw-scale-95 tw-transition-all tw-duration-200'
      : 'tw-bg-gray-200 dark:tw-bg-gray-800 tw-text-gray-400 dark:tw-text-gray-500 tw-cursor-not-allowed';
  }



  updateMessageStatus(messageId: string, status: 'sent' | 'delivered' | 'read') {
    this.messages.update(prev =>
      prev.map(msg =>
        msg._id === messageId ? { ...msg, status } : msg
      )
    );
  }

  onMessageContextMenu(event: MouseEvent, message: Message, trigger: MatMenuTrigger) {
    if (message.isDeleted) {
      return;
    }
    event.preventDefault();
    this.selectedMessage.set(message);
    trigger.openMenu();
  }

  onReplyClick(message: Message) {
    if (message.isDeleted) {
      this.toastService.warning('Cannot reply to deleted messages');
      return;
    }
    this.setReplyMessage(message);
  }

  onDeleteClick(message: Message) {
    this.deleteMessage(message);
  }

  copyMessage(message: Message) {
    if (this.isBrowser()) {
      navigator.clipboard.writeText(message.content).then(() => {
        this.toastService.success('Message copied');
      });
    }
  }

  editMessage(message: Message) {
    if (message?.userId != this.loggedUser?.id) return;

    const dialogRef = this.dialog.open(EditMessageDialogComponent, {
      width: '500px',
      data: { message }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.newContent && result.newContent !== message.content) {
        const activeId = this.activeConversation();
        if (activeId) {
          this.socketService.editMessage(message._id, result.newContent, activeId);
        }
      }
    });
  }

  addReaction(messageId: string, emoji: string) {
    this.messages.update(prev =>
      prev.map(msg =>
        msg._id === messageId
          ? { ...msg, reactions: [...(msg.reactions || []), emoji] }
          : msg
      )
    );
  }

  updateMessageDeliveryStatus(messageId: string) {
    setTimeout(() => {
      this.updateMessageStatus(messageId, 'delivered');
      setTimeout(() => {
        this.updateMessageStatus(messageId, 'read');
      }, 1000 + Math.random() * 2000);
    }, 500);
  }


  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcuts(event: KeyboardEvent) {
    if (!this.activeConversation()) return;

    if (event.key === 'Escape' && this.replyToMessage()) {
      this.cancelReply();
      event.preventDefault();
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      this.showChatSearch.set(true);
      event.preventDefault();
    }
  }

  toggleReaction(messageId: string, emoji: string) {
    this.messages.update(prev =>
      prev.map(msg => {
        if (msg._id === messageId) {
          const reactions = msg.reactions || [];
          const hasReaction = reactions.includes(emoji);

          return {
            ...msg,
            reactions: hasReaction
              ? reactions.filter(r => r !== emoji)
              : [...reactions, emoji]
          };
        }
        return msg;
      })
    );
  }


  simulateOtherUserTyping(conversationId: string) {
    const currentConversations = this.conversations();
    const conversationIndex = currentConversations.findIndex(c => c._id === conversationId);

    if (conversationIndex !== -1) {
      const updatedConversations = currentConversations.map((conv, index) => {
        if (index === conversationIndex) {
          return { ...conv, isTyping: true };
        }
        return conv;
      });
      this.conversations.set(updatedConversations);

      setTimeout(() => {
        const finalConversations = this.conversations().map((conv, index) => {
          if (index === conversationIndex) {
            return { ...conv, isTyping: false };
          }
          return conv;
        });
        this.conversations.set(finalConversations);
      }, 2000 + Math.random() * 3000);
    }
  }

  markMessagesAsRead(conversationId: string) {
    this.messages.update(prev =>
      prev.map(msg => ({
        ...msg,
        isRead: (msg?.userId == this.loggedUser?.id) ? msg.isRead : true
      }))
    );

    this.messagesService.markMessagesAsRead(conversationId).subscribe({
      next: () => {
        console.log('Messages marked as read');
      },
      error: (error) => {
        console.error('Error marking messages as read:', error);
      }
    });
  }

  onMessagesScroll(event: Event) {
    const container = event.target as HTMLElement;

    if (container.scrollTop < 100 && this.hasMoreMessages && !this.isLoading()) {
      this.loadOlderMessages();
    }

    const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 50;
    this.shouldAutoScroll = isAtBottom;
    this.isNearBottom = isAtBottom;
  }


  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectedFiles.set(Array.from(files));
      this.openFileUploadSheet();
    }
  }

  @HostListener('window:online')
  onOnline() {
    this.connectionStatus.set('online');
  }

  @HostListener('window:offline')
  onOffline() {
    this.connectionStatus.set('offline');
  }

  openUserSelectionModal() {
    const dialogRef = this.dialog.open(UserSelectionModalComponent, {
      width: '800px',
      maxWidth: '98vw',
      maxHeight: '98vh',
      autoFocus: false,
      restoreFocus: false,
      panelClass: ['safety-knights-dialog'],
      hasBackdrop: true,
      data: {
        currentConversations: this.conversations
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadUserRequests();
    });
  }

  handleNewConversation(user: any) {
    console.log('12121212', user);

    const currentConversations = this.conversations();

    const existingConv = currentConversations.find(conv =>
      conv.email === user.email ||
      (conv.firstName === user.firstName && conv.lastName === user.lastName)
    );

    if (!existingConv) {
      this.socketService.createConversation(user._id, user);
      this.toastService.info(`Creating conversation with ${user.firstName}...`);
    } else {
      const userName = `${existingConv.firstName}${existingConv.lastName}`.toLowerCase().replace(/\s+/g, '');
      this.router.navigate(['/messages', userName]);

      this.toastService.info(`Opened existing conversation with ${user.firstName}`);
    }
  }


  navigateToMessage(userName: string, messageId: string) {
    this.router.navigate(['/messages', userName, messageId]);
  }


  get totalConversations(): number {
    return this.conversations().length;
  }

  get filteredUnreadConversations(): number {
    return this.conversations().filter(c => c.unreadCount > 0).length;
  }

  get filteredOnlineConversations(): number {
    return this.conversations().filter(c => c.isOnline).length;
  }

  formatTime(timeValue: string | Date): string {
    if (typeof timeValue === 'string' && timeValue.length <= 8 && !timeValue.includes('T')) {
      return timeValue;
    }

    try {
      const date = new Date(timeValue);
      if (isNaN(date.getTime())) return 'Invalid Time';

      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch (error) {
      return 'Invalid Time';
    }
  }

  formatDateLabel(date: string | Date): string {
    const d = new Date(date);
    const today = new Date();

    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    }

    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }

    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }

  shouldShowDateSeparator(messages: any[], index: number): boolean {
    if (index === 0) return true;
    const currentDate = new Date(messages[index].createdAt).toDateString();
    const prevDate = new Date(messages[index - 1].createdAt).toDateString();
    return currentDate !== prevDate;
  }

  setFilter(filter: 'all' | 'unread' | 'active') {
    this.activeFilter.set(filter);
  }

  getFilterButtonClasses(filterType: 'all' | 'unread' | 'active'): string {
    const isActive = this.activeFilter() === filterType;
    if (isActive) {
      return 'tw-group tw-px-4 tw-py-2.5 tw-text-xs tw-font-bold tw-rounded-xl tw-bg-gradient-to-r tw-from-indigo-500 tw-to-indigo-600 tw-text-white tw-border-0 tw-shadow-lg tw-shadow-indigo-500/25 tw-transition-all tw-duration-200 hover:tw-scale-105 tw-active:scale-95 focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/20';
    }
    return 'tw-group tw-px-4 tw-py-2.5 tw-text-xs tw-font-bold tw-rounded-xl tw-bg-white/80 dark:tw-bg-slate-800/80 tw-text-slate-600 dark:tw-text-slate-400 tw-border tw-border-slate-200/60 dark:tw-border-slate-700/60 hover:tw-bg-slate-50 dark:hover:tw-bg-slate-700/80 hover:tw-border-slate-300/60 dark:hover:tw-border-slate-600/60 tw-transition-all tw-duration-200 hover:tw-scale-105 tw-active:scale-95 focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-slate-500/10';
  }

  formatFullTime(date: Date | string): string {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(new Date(date));
  }

  getReadStatusLabel(isRead: boolean): string {
    return isRead ? 'Message read' : 'Message sent';
  }

  loadImageProgressively(message: ImageMessage) {
    if (message.thumbnail) {
      const img = new Image();
      img.onload = () => {
        message.isLoading = false;
      };
      img.src = message.thumbnail;
    }

    if (message.fullImage) {
      const fullImg = new Image();
      fullImg.onload = () => {
        message.fullImage = fullImg.src;
        this.cdr.markForCheck();
      };
      fullImg.src = message.fullImage;
    }
  }

  initializeAudioPlayer(message: any): void {
    if (!message.fileUrl) return;
    message.showAudioPlayer = true;
    message.isLoading = true;
    message.currentTime = 0;
    message.duration = 0;
    message.isPlaying = false;
    setTimeout(() => this.toggleAudioPlayback(message), 100);
  }

  toggleAudioPlayback(message: any): void {
    const audioElement = this.getAudioElement(message);
    if (!audioElement) return;

    if (message.isPlaying) {
      this.pauseAudio(message, audioElement);
    } else {
      this.playAudio(message, audioElement);
    }
  }

  private getAudioElement(message: any): HTMLAudioElement | null {
    const audioId = message._id;
    if (this.audioElements.has(audioId)) {
      return this.audioElements.get(audioId)!;
    }
    const audioElement = document.querySelector(`audio[src="${message.fileUrl}"]`) as HTMLAudioElement;
    if (audioElement) {
      this.audioElements.set(audioId, audioElement);
      return audioElement;
    }
    return null;
  }

  private playAudio(message: any, audioElement: HTMLAudioElement): void {
    message.isLoading = true;

    audioElement.play()
      .then(() => {
        message.isPlaying = true;
        message.isLoading = false;
      })
      .catch((error) => {
        console.error('Audio play failed:', error);
        message.isLoading = false;
        message.isPlaying = false;
      });
  }

  private pauseAudio(message: any, audioElement: HTMLAudioElement): void {
    audioElement.pause();
    message.isPlaying = false;
    message.isLoading = false;
  }

  onAudioTimeUpdate(message: any, audioPlayer: HTMLAudioElement): void {
    message.currentTime = audioPlayer.currentTime;
  }

  onAudioLoaded(message: any, audioPlayer: HTMLAudioElement): void {
    message.duration = audioPlayer.duration;
    message.isLoading = false;
    this.audioElements.set(message._id, audioPlayer);
  }

  onAudioCanPlay(message: any): void {
    message.isLoading = false;
  }

  onAudioError(message: any): void {
    console.error('Audio loading error for message:', message._id);
    message.isLoading = false;
    message.isPlaying = false;
    message.hasError = true;
  }

  onAudioEnded(message: any): void {
    message.isPlaying = false;
    message.currentTime = 0;
  }

  seekAudio(message: any, event: MouseEvent): void {
    const audioElement = this.getAudioElement(message);
    if (!audioElement || !message.duration) return;

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    const newTime = percent * message.duration;

    audioElement.currentTime = newTime;
    message.currentTime = newTime;
  }

  getAudioProgress(message: any): number {
    if (!message.duration || message.duration === 0) return 0;
    return (message.currentTime || 0) / message.duration * 100;
  }

  formatAudioTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  getMediaTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      'image': 'Image',
      'video': 'Video',
      'audio': 'Audio',
      'file': 'File'
    };
    return labels[type] || 'File';
  }
  formatConversationTime(timeValue: string | Date): string {
    try {
      const messageDate = new Date(timeValue);
      if (isNaN(messageDate.getTime())) return 'Invalid Time';

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);

      const messageDateOnly = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());

      // Today - show time only
      if (messageDateOnly.getTime() === today.getTime()) {
        return messageDate.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }

      // Yesterday - show "Yesterday"
      if (messageDateOnly.getTime() === yesterday.getTime()) {
        return 'Yesterday';
      }

      // Older than yesterday - show date
      const thisYear = now.getFullYear();
      const messageYear = messageDate.getFullYear();

      // If same year, show month and day
      if (messageYear === thisYear) {
        return messageDate.toLocaleDateString([], {
          month: 'short',
          day: 'numeric'
        });
      }

      // If different year, show month, day, and year
      return messageDate.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

    } catch (error) {
      console.error('Error formatting conversation time:', error);
      return 'Invalid Time';
    }
  }
  getEmptyStateContent(): { title: string, message: string, icon: string } {
    switch (this.activeFilter()) {
      case 'unread':
        return {
          title: 'No unread conversations',
          message: 'All caught up! No new messages to read.',
          icon: 'heroEnvelope'
        };
      case 'active':
        return {
          title: 'No active users online',
          message: 'No users are currently online. Check back later or start a new conversation.',
          icon: 'heroSignal'
        };
      default: // 'all'
        return {
          title: 'No conversations yet',
          message: 'Connect with companies and recruiters to start meaningful conversations.',
          icon: 'heroChatBubbleLeftEllipsis'
        };
    }
  }
  scrollToRepliedMessage(messageId: string): void {
    // Clear any existing search highlights first
    this.clearChatSearch();

    setTimeout(() => {
      const messageElement = document.getElementById(`message-${messageId}`);
      if (!messageElement) {
        // Try to find the message in your messages array first
        const foundMessage = this.messages().find(m => m._id === messageId);
        if (!foundMessage) {
          this.toastService.info('Message not found in current conversation');
          return;
        }

        // If message exists but not visible, you might need to load more messages
        this.toastService.info('Loading more messages...');
        // Load more messages logic here if needed
        return;
      }

      // Remove any existing highlights
      document.querySelectorAll('.message-highlight, .search-highlight').forEach(el => {
        el.classList.remove('message-highlight', 'search-highlight');
      });

      // Scroll and highlight
      messageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });

      messageElement.classList.add('message-highlight');

      setTimeout(() => {
        messageElement.classList.remove('message-highlight');
      }, 3000);
    }, 100);
  }

  // Add these methods to your MessagesComponent
  async loadFileOnDemand(message: Message, maxRetries: number = 3) {
    if (!message.fileId || message.isFileLoading) return;

    message.isFileLoading = true;
    message.fileLoadError = false;
    message.retryCount = message.retryCount || 0;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Loading file attempt ${attempt + 1}/${maxRetries + 1} for file ${message.fileId}`);

        // Add exponential backoff delay for retries
        if (attempt > 0) {
          const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s delays
          console.log(`Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        const fileData = await this.socketService.getFile(message.fileId, message.fileSize);

        // Success - update message
        message.fileUrl = fileData.data;
        message.fileName = fileData.fileName;
        message.fileSize = fileData.size;
        message.contentType = fileData.contentType;
        message.retryCount = 0; // Reset on success

        // Update the messages array
        this.messages.update(messages =>
          messages.map(msg => msg._id === message._id ? message : msg)
        );

        console.log(`File loaded successfully on attempt ${attempt + 1}`);
        break; // Success, exit retry loop

      } catch (error) {
        console.warn(`File load attempt ${attempt + 1} failed:`, error);

        if (attempt === maxRetries) {
          // Final attempt failed
          message.fileLoadError = true;
          message.retryCount = attempt + 1;
          console.error(`Failed to load file after ${maxRetries + 1} attempts:`, error);
        }
      }
    }

    message.isFileLoading = false;
  }


  // For images specifically
  async loadImageOnDemand(message: any) {
    await this.loadFileOnDemand(message);
  }

  // For videos
  async loadVideoOnDemand(message: any) {
    await this.loadFileOnDemand(message);
  }

  // For audio files
  async loadAudioOnDemand(message: any) {
    await this.loadFileOnDemand(message);
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}